import {
    useGetTransferRequestByCodeQuery,
    useResolveBillingTransferRequestMutation,
} from "@api/billingTransfer.api";
import { useWorkspaceDetails } from "@api/workspace.api";
import {
    BillingTransferRequestResolution,
    BillingTransferRequestStatus,
    BillingTransferRequestType,
} from "@common/constants/domains/billing/billingTransferRequest";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import { StateAwareNavigate } from "@components/routes/StateAwareNavigate";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { FC, useEffect } from "react";
import { useParams } from "react-router-dom";

interface BillingTransferResolutionParams {
    code: string;
    resolution: BillingTransferRequestResolution;
}

export const BillingTransferResolution: FC = () => {
    const { code, resolution } =
        useParams() as Readonly<BillingTransferResolutionParams>;
    const { toast } = useToaster();
    const navigate = useStateAwareNavigate();
    const activeOrganization = useActiveOrganization();
    const { changeActiveWorkspace, changeActiveOrganization } = useScope();
    const { data: transferRequest, isLoading } =
        useGetTransferRequestByCodeQuery(code);
    const { data: workspaceDetails } = useWorkspaceDetails(
        transferRequest?.workspaceId ?? undefined,
    );

    const resolver = useResolveBillingTransferRequestMutation({
        onSuccess: () => {
            // fallback, mostly for type safety
            if (!transferRequest) {
                return navigate("/");
            }

            const workspaceDetailsForRequest = workspaceDetails;
            const action =
                resolution === BillingTransferRequestStatus.ACCEPTED
                    ? "accepted"
                    : "declined";
            let message = `You've ${action} billing transfer`;

            if (
                transferRequest.type ===
                BillingTransferRequestType.WORKSPACE_TO_ORGANIZATION
            ) {
                // workspace primary admin resovled the request

                const organizationForRequest =
                    workspaceDetailsForRequest?.organizationAssignments.find(
                        (assignment) =>
                            assignment.organizationId ===
                            transferRequest.organizationId,
                    )?.organization;

                if (organizationForRequest) {
                    message += ` to ${organizationForRequest.name}`;
                }

                toast(message);

                if (workspaceDetailsForRequest) {
                    changeActiveWorkspace(
                        workspaceDetailsForRequest.workspace.id,
                    );
                    navigate(`/settings/billing`);
                } else {
                    navigate("/");
                }
            } else {
                // organization owner resovled the request

                if (workspaceDetailsForRequest) {
                    message += ` to ${workspaceDetailsForRequest.workspace.name}`;
                }

                toast(message);

                if (activeOrganization) {
                    // switch to general organization view
                    changeActiveOrganization(activeOrganization);
                }

                navigate("/");
            }
        },
    });

    useEffect(() => {
        if (!resolver.isIdle) {
            return;
        }

        if (transferRequest?.id) {
            resolver.mutate({
                requestId: transferRequest.id,
                status: resolution,
            });
        }
    }, [transferRequest?.id, resolution, resolver]);

    if (isLoading || resolver.isPending) {
        return <PageCoverLoader />;
    }

    if (!transferRequest) {
        return <StateAwareNavigate to="/" replace />;
    }

    return null;
};
